'use client';

import { type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import BevSafeText from '../bev-safe-text';
import getCtaStyles from '../button/get-cta-styles';
import RichTextTitle, { type HeadingLevel } from '../rich-text-title';
import LocationTile from './location-tile';
import type { LocationTileData } from './types';

const MAX_TILES = 5;
const MIN_TILES = 2;
const BALANCED_TILE_COUNT = 2;

// Mirrors the authored Blacklight property name (location-banner.js sets
// `model.titleHeadingLevel`), so the prop stays a 1:1 passthrough of the model.
export type TitleHeadingLevel = HeadingLevel;

export type Props = {
	tiles: LocationTileData[];
	title?: string;
	titleHeadingLevel?: TitleHeadingLevel;
	description?: string;
	id?: string;
	sectionId?: string;
	directionsHref?: string;
	directionsLabel?: string;
	directionsNewWindow?: boolean;
	contactHref?: string;
	contactLabel?: string;
	contactNewWindow?: boolean;
	className?: string;
};

export default function LocationBanner({
	tiles,
	title,
	titleHeadingLevel = 'h2',
	description,
	id,
	sectionId,
	directionsHref,
	directionsLabel,
	directionsNewWindow,
	contactHref,
	contactLabel,
	contactNewWindow,
	className,
}: Props): ReactElement | null {
	const t = useTranslations();
	const items = tiles.slice(0, MAX_TILES);
	const count = items.length;

	if (count < MIN_TILES) return null;

	const isBalanced = count === BALANCED_TILE_COUNT;
	const rowGap = isBalanced ? 'md:gap-x-inline-2xl' : 'md:gap-x-inline-l';
	const dividerPadding = isBalanced ? 'md:pl-inline-2xl' : 'md:pl-inline-l';
	const hasHeader = Boolean(title || description);
	const hasCtas = Boolean(directionsHref || contactHref);

	return (
		<section id={sectionId} className={cn('w-full spacing-wide-grid', className)}>
			<div className="flex w-full flex-col gap-y-stack-l">
				{(hasHeader || hasCtas) && (
					<div className="flex flex-col gap-y-stack-l gap-x-300 md:flex-row md:items-center md:justify-between">
						{hasHeader && (
							<div className="flex flex-col gap-y-heading-s md:w-[723px]">
								{title && (
									<RichTextTitle
										title={title}
										headingLevel={titleHeadingLevel}
										id={id}
										className="text-h2 line-clamp-2 text-text-heading"
									/>
								)}
								{description && (
									<p className="text-body1 text-text-primary">
										<BevSafeText>{description}</BevSafeText>
									</p>
								)}
							</div>
						)}

						{hasCtas && (
							<div className="flex gap-100 md:flex-col md:items-stretch">
								{directionsHref && (
									<a
										href={directionsHref}
										target={directionsNewWindow ? '_blank' : undefined}
										rel={directionsNewWindow ? 'noopener' : undefined}
										className={getCtaStyles({
											intent: 'filled',
											size: 'small',
											className: 'flex-1 md:flex-none md:whitespace-nowrap',
										})}
									>
										{directionsLabel ?? t('How to get here')}
									</a>
								)}
								{contactHref && (
									<a
										href={contactHref}
										target={contactNewWindow ? '_blank' : undefined}
										rel={contactNewWindow ? 'noopener' : undefined}
										className={getCtaStyles({
											intent: 'outline',
											size: 'small',
											className: 'flex-1 md:flex-none md:whitespace-nowrap',
										})}
									>
										{contactLabel ?? t('Contact Us')}
									</a>
								)}
							</div>
						)}
					</div>
				)}

				{(hasHeader || hasCtas) && <div className="h-px w-full bg-border-subtle" />}

				<ul className={cn('flex flex-col gap-y-stack-2xl md:flex-row md:items-stretch', rowGap)}>
					{items.map((tile, index) => (
						<LocationTile
							key={tile.id}
							tile={tile}
							isFirst={index === 0}
							dividerPaddingClass={dividerPadding}
						/>
					))}
				</ul>
			</div>
		</section>
	);
}
